import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AlertTriangle, ChevronRight, Loader2, Plus, Receipt, RefreshCw, Search, X } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { formatDay } from "../lib/finance";
import { PERIODICITY_LABELS, SUBSCRIPTION_STATUS_LABELS, billingError, canWrite, money, priceText } from "../lib/billing";
import { RevenueTabs, SubscriptionStatusBadge } from "../components/billing/BillingBits";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Skeleton } from "../components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const ALL = "__all__";
const PAGE_SIZE = 50;
const STATES = [{ id: "open", label: "En cours et brouillons" }, { id: "closed", label: "Terminés" }];

function useFilters() {
  const [params, setParams] = useSearchParams();
  const filters = {
    state: ["open", "closed"].includes(params.get("state")) ? params.get("state") : (params.get("status") || params.get("trial_ended") ? "" : "open"),
    status: Object.keys(SUBSCRIPTION_STATUS_LABELS).includes(params.get("status")) ? params.get("status") : "",
    offer: params.get("offer") || "",
    prospect: params.get("prospect") || "",
    trialEnded: params.get("trial_ended") === "1" ? "1" : "",
    search: params.get("q") || "",
  };
  const update = useCallback((changes) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [key, value] of Object.entries(changes)) {
        const name = { search: "q", trialEnded: "trial_ended" }[key] || key;
        if (value === "" || value == null) next.delete(name);
        else next.set(name, value);
      }
      return next;
    }, { replace: true });
  }, [setParams]);
  return [filters, update];
}

export default function BillingSubscriptions() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [filters, update] = useFilters();
  const [searchInput, setSearchInput] = useState(filters.search);
  const [data, setData] = useState({ items: [], total: 0 });
  const [offers, setOffers] = useState([]);
  const [phase, setPhase] = useState("loading");
  const [error, setError] = useState("");
  const [loadingMore, setLoadingMore] = useState(false);
  const seq = useRef(0);
  const canCreate = canWrite(user, "billing:manage_subscriptions");

  useEffect(() => {
    const t = setTimeout(() => { if (searchInput !== filters.search) update({ search: searchInput.trim() }); }, 300);
    return () => clearTimeout(t);
  }, [searchInput]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { api.get("/billing/offers").then(({ data: body }) => setOffers(body.items)).catch(() => {}); }, []);

  const query = useMemo(() => {
    const out = { limit: PAGE_SIZE };
    if (filters.state) out.state = filters.state;
    if (filters.status) out.status = filters.status;
    if (filters.offer) out.offer_id = filters.offer;
    if (filters.prospect) out.prospect_id = filters.prospect;
    if (filters.trialEnded) out.trial_ended = true;
    if (filters.search) out.search = filters.search;
    return out;
  }, [filters.state, filters.status, filters.offer, filters.prospect, filters.trialEnded, filters.search]);

  const load = useCallback(async () => {
    const mine = ++seq.current;
    setPhase("loading");
    try {
      const { data: body } = await api.get("/billing/subscriptions", { params: query });
      if (mine !== seq.current) return;
      setData(body);
      setPhase("ready");
    } catch (e) {
      if (mine !== seq.current) return;
      setError(billingError(e, "Impossible de charger les abonnements."));
      setPhase("error");
    }
  }, [query]);
  useEffect(() => { load(); return () => { seq.current += 1; }; }, [load]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const { data: body } = await api.get("/billing/subscriptions", { params: { ...query, skip: data.items.length } });
      setData((prev) => ({ ...body, items: [...prev.items, ...body.items.filter((i) => !prev.items.some((p) => p.id === i.id))] }));
    } catch (e) {
      toast.error(billingError(e, "Impossible de charger la suite."));
    } finally {
      setLoadingMore(false);
    }
  };

  const filtered = !!(filters.status || filters.offer || filters.prospect || filters.trialEnded || filters.search);
  const reset = () => { setSearchInput(""); update({ status: "", offer: "", prospect: "", trialEnded: "", search: "", state: "open" }); };
  const statuses = filters.state === "closed" ? ["ended", "cancelled"] : filters.state === "open" ? ["draft", "trial", "active", "paused"] : Object.keys(SUBSCRIPTION_STATUS_LABELS);

  return (
    <div className="space-y-4" data-testid="billing-subscriptions-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-display font-bold text-2xl text-slate-900">Offres et abonnements</h1>
          <p className="text-sm text-slate-500">Ce que chaque pharmacie cliente paie : son offre, son prix, sa périodicité et ses échéances.</p>
        </div>
        {canCreate && (
          <Button asChild data-testid="billing-subscription-new">
            <Link to={filters.prospect ? `/revenus/abonnements/nouveau?prospect=${filters.prospect}` : "/revenus/abonnements/nouveau"}><Plus size={16} className="mr-1.5" />Nouvel abonnement</Link>
          </Button>
        )}
      </div>
      <RevenueTabs />

      <div className="flex gap-1 flex-wrap" role="tablist" aria-label="Abonnements">
        {STATES.map((s) => (
          <button key={s.id} type="button" role="tab" aria-selected={filters.state === s.id && !filters.trialEnded}
            onClick={() => update({ state: s.id, status: "", trialEnded: "" })} data-testid={`billing-subs-state-${s.id}`}
            className={`rounded-full px-3 py-1.5 text-sm font-medium whitespace-nowrap ${filters.state === s.id && !filters.trialEnded ? "bg-jamora text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`}>
            {s.label}
          </button>
        ))}
        <button type="button" role="tab" aria-selected={!!filters.trialEnded} onClick={() => update({ trialEnded: filters.trialEnded ? "" : "1", state: filters.trialEnded ? "open" : "", status: "" })}
          data-testid="billing-subs-trials" className={`rounded-full px-3 py-1.5 text-sm font-medium whitespace-nowrap ${filters.trialEnded ? "bg-jamora text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`}>
          Essais terminés
        </button>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative basis-full sm:basis-auto sm:flex-1 min-w-0 md:max-w-xs">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
          <Input value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Numéro ou pharmacie…" className="pl-9 h-9"
            aria-label="Rechercher un abonnement" data-testid="billing-subs-search" />
        </div>
        <Select value={filters.status || ALL} onValueChange={(v) => update({ status: v === ALL ? "" : v })}>
          <SelectTrigger className="h-9 w-[calc(50%-4px)] sm:w-44" aria-label="État"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Tous les états</SelectItem>
            {statuses.map((s) => <SelectItem key={s} value={s}>{SUBSCRIPTION_STATUS_LABELS[s]}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.offer || ALL} onValueChange={(v) => update({ offer: v === ALL ? "" : v })}>
          <SelectTrigger className="h-9 w-[calc(50%-4px)] sm:w-44" aria-label="Offre"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Toutes les offres</SelectItem>
            {offers.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
          </SelectContent>
        </Select>
        {filtered && <Button variant="ghost" size="sm" className="h-9 text-slate-600" onClick={reset}><X size={14} className="mr-1" />Effacer</Button>}
      </div>

      {phase === "loading" && <div className="space-y-2"><Skeleton className="h-12 w-full" /><Skeleton className="h-12 w-full" /></div>}
      {phase === "error" && (
        <Card className="p-6 text-center space-y-3">
          <AlertTriangle className="mx-auto text-amber-500" size={20} />
          <p className="text-sm text-slate-700">{error}</p>
          <Button variant="outline" onClick={load}><RefreshCw size={14} className="mr-1.5" />Réessayer</Button>
        </Card>
      )}
      {phase === "ready" && data.items.length === 0 && (
        <Card className="p-8 text-center space-y-2" data-testid="billing-subs-empty">
          <Receipt className="mx-auto text-slate-300" size={26} />
          {filtered ? (
            <><h3 className="font-medium text-slate-900">Aucun abonnement ne correspond</h3>
              <Button variant="outline" size="sm" onClick={reset}>Effacer les filtres</Button></>
          ) : (
            <><h3 className="font-medium text-slate-900">Aucun abonnement client {filters.state === "closed" ? "terminé" : "en cours"}</h3>
              <p className="text-sm text-slate-500 max-w-md mx-auto">Abonnez une pharmacie cliente à une offre publiée : ses échéances se créeront à chaque période.</p></>
          )}
        </Card>
      )}
      {phase === "ready" && data.items.length > 0 && (
        <>
          <Card className="hidden md:block overflow-hidden" data-testid="billing-subs-table">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Pharmacie</TableHead><TableHead>Offre</TableHead><TableHead>Prix</TableHead><TableHead>État</TableHead>
                  <TableHead className="hidden lg:table-cell">Début</TableHead><TableHead className="hidden xl:table-cell text-right">Par mois</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((s) => (
                  <TableRow key={s.id} className="cursor-pointer" onClick={() => navigate(`/revenus/abonnements/${s.id}`)} data-testid={`billing-sub-row-${s.number}`}>
                    <TableCell>
                      <Link to={`/revenus/abonnements/${s.id}`} className="font-medium text-slate-900 hover:underline" onClick={(e) => e.stopPropagation()}>{s.pharmacy_name}</Link>
                      <div className="text-xs text-slate-500 tabular-nums">{s.number}</div>
                    </TableCell>
                    <TableCell className="text-sm">
                      {s.offer.offer_name} <span className="text-slate-400">v{s.offer.version_number}</span>
                      {s.newer_version && <div className="text-xs text-amber-700" data-testid={`billing-newer-${s.number}`}>Version {s.newer_version.number} en vente</div>}
                    </TableCell>
                    <TableCell className="text-sm tabular-nums">{priceText(s.price_minor, s.currency, s.periodicity)}{s.negotiated && <span className="ml-1 text-xs text-amber-700">négocié</span>}</TableCell>
                    <TableCell><SubscriptionStatusBadge status={s.status} /></TableCell>
                    <TableCell className="hidden lg:table-cell text-sm tabular-nums">{formatDay(s.start_date)}</TableCell>
                    <TableCell className="hidden xl:table-cell text-sm tabular-nums text-right">{money(s.monthly_minor, s.currency)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
          <Card className="md:hidden divide-y divide-slate-100" data-testid="billing-subs-cards">
            {data.items.map((s) => (
              <Link key={s.id} to={`/revenus/abonnements/${s.id}`} className="flex items-start gap-3 px-4 py-3" data-testid={`billing-sub-card-${s.number}`}>
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="font-medium text-slate-900">{s.pharmacy_name}</div>
                  <div className="text-xs text-slate-500">{s.number} · {s.offer.offer_name} · {PERIODICITY_LABELS[s.periodicity].toLowerCase()}</div>
                  <div className="flex flex-wrap items-center gap-2 text-sm tabular-nums">{priceText(s.price_minor, s.currency, s.periodicity)}<SubscriptionStatusBadge status={s.status} /></div>
                </div>
                <ChevronRight size={16} className="text-slate-300 shrink-0 mt-1" />
              </Link>
            ))}
          </Card>
          <div className="flex items-center justify-between gap-3 text-xs text-slate-500">
            <span data-testid="billing-subs-count">{data.items.length} sur {data.total} abonnement{data.total > 1 ? "s" : ""}</span>
            {data.items.length < data.total && (
              <Button variant="outline" size="sm" onClick={loadMore} disabled={loadingMore}>{loadingMore && <Loader2 size={14} className="mr-1.5 animate-spin" />}Charger plus</Button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
